import { request } from './api';

type MetaPixelUserData = Record<string, unknown>;
type MetaPixelCustomData = Record<string, unknown>;

interface MetaPixelPayload {
  action_source?: string;
  event_id?: string;
  event_source_url?: string;
  custom_data?: MetaPixelCustomData;
  user_data?: MetaPixelUserData;
  test_event_code?: string;
}

function getStorage() {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function hasMarketingConsent(): boolean {
  const storage = getStorage();
  if (!storage) return false;

  const rawConsent = storage.getItem('cookie_consent');
  if (!rawConsent) return false;

  try {
    const consent = JSON.parse(rawConsent);
    return consent?.marketing === true;
  } catch {
    return false;
  }
}

function getCurrentPageUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.href;
}

function cleanObject<T extends Record<string, unknown>>(value: T | undefined): T | undefined {
  if (!value) return undefined;

  const cleaned = Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
  ) as T;

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function getMetaPixelStorageKey(eventName: string, uniqueKey: string) {
  return `meta-pixel:${eventName}:${uniqueKey}`;
}

export function getMetaPixelUserData(
  user?: { id?: string; email?: string } | null
): MetaPixelUserData {
  return (
    cleanObject({
      em: user?.email,
      external_id: user?.id,
    }) || {}
  );
}

export function hasTrackedMetaPixelEvent(eventName: string, uniqueKey: string): boolean {
  const storage = getStorage();
  if (!storage || !uniqueKey) return false;
  return storage.getItem(getMetaPixelStorageKey(eventName, uniqueKey)) === '1';
}

export function markMetaPixelEventTracked(eventName: string, uniqueKey: string) {
  const storage = getStorage();
  if (!storage || !uniqueKey) return;
  storage.setItem(getMetaPixelStorageKey(eventName, uniqueKey), '1');
}

async function postMetaPixel(url: string, payload: MetaPixelPayload) {
  if (!hasMarketingConsent()) {
    return null;
  }

  return request(url, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      action_source: payload.action_source || 'website',
      event_source_url: payload.event_source_url || getCurrentPageUrl(),
      custom_data: cleanObject(payload.custom_data),
      user_data: cleanObject(payload.user_data) || {},
    }),
  });
}

export async function trackCompleteRegistration(payload: MetaPixelPayload = {}) {
  return postMetaPixel('/meta-pixel/complete-registration', payload);
}

export async function trackSubscribe(payload: MetaPixelPayload = {}) {
  return postMetaPixel('/meta-pixel/subscribe', payload);
}

export async function trackStartTrial(payload: MetaPixelPayload = {}) {
  return postMetaPixel('/meta-pixel/start-trial', payload);
}
